import React, { useState } from "react";
import { EvtStatsCardPlaceholder } from "components/stats/placeholder";
import { EvtStatsCard } from "components/stats/display-card";
import { DisplayPercentage } from "components/display/percentage";
import { DisplayAmount } from "components/display/amount";
import { DisplayHours } from "components/display/hours";
import { usePermissions } from "contexts/permission";
import { useFetch } from "@/hooks/useFetch";
import { useProject } from "../../../context";
import { fetchProjectRevenueCalculation } from "../../api";
import { DASHBOARD_STATS_INFO } from "../constants";
import {
  DashboardStatsGroup,
  DashboardStatsSubSection,
  DashboardStatsErrorSubSection,
} from "@/modules/work/shared/dashboard-stats-group";
import CalendarDatePicker from "ui/components/comp-507";
import { BillableHoursWarning } from "@/modules/work/shared/billable-hours-warning";
import { JobTagsFilterCombobox } from "@/modules/work/shared/job-tag-filter";

// Sub-sections only (no group), so loading can render inside the same
// DashboardStatsGroup as loaded/error states and the filter never remounts.
const StatsPlaceholderContent = ({ canReadAmount }) => (
  <>
    <DashboardStatsSubSection title="Financials">
      <EvtStatsCardPlaceholder />
      {canReadAmount &&
        Array.from({ length: 8 }, (_, i) => <EvtStatsCardPlaceholder key={i} />)}
    </DashboardStatsSubSection>
    <DashboardStatsSubSection title="Delivery">
      <EvtStatsCardPlaceholder />
    </DashboardStatsSubSection>
  </>
);

export const ProjectDashboardStatsSection = () => {
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  });
  const [jobTagId, setJobTagId] = useState();
  const { id } = useProject();
  const permissions = usePermissions();

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  const isDateRangeComplete = Boolean(dateRange?.from && dateRange?.to);
  const startDate = isDateRangeComplete ? dateRange.from : undefined;
  const endDate = isDateRangeComplete ? dateRange.to : undefined;

  const { data, isLoading, isError, refetch } = useFetch({
    queryKey: [
      "revenue-calculation-project-dashboard",
      id,
      startDate,
      endDate,
      jobTagId,
    ],
    queryFn: () =>
      fetchProjectRevenueCalculation({
        projectId: id,
        startDate,
        endDate,
        jobTagId,
      }),
    enabled: !!id,
  });

  const dateFilter = (
    <div className="flex items-center gap-2">
      <JobTagsFilterCombobox value={jobTagId} onChange={setJobTagId} />
      <CalendarDatePicker date={dateRange} setDate={setDateRange} />
    </div>
  );

  if (isLoading) {
    return (
      <DashboardStatsGroup action={dateFilter} label="Project metrics">
        <StatsPlaceholderContent canReadAmount={canReadAmount} />
      </DashboardStatsGroup>
    );
  }
  if (isError) {
    return (
      <DashboardStatsGroup action={dateFilter} label="Project metrics">
        {canReadAmount && (
          <DashboardStatsErrorSubSection title="Financials" onRetry={refetch} />
        )}
        <DashboardStatsErrorSubSection title="Delivery" onRetry={refetch} />
      </DashboardStatsGroup>
    );
  }

  return (
    <>
    <BillableHoursWarning validation={data?.validation} />
    <DashboardStatsGroup action={dateFilter} label="Project metrics">
      <DashboardStatsSubSection title="Financials">
        <EvtStatsCard
          label="Project Health"
          value={
            <DisplayPercentage
              value={data?.margin_achievement_percentage}
              precision={0}
            />
          }
          info={DASHBOARD_STATS_INFO.budgetVsAchieved}
        />
        {canReadAmount && (
          <>
            <EvtStatsCard
              label="Total Revenue"
              value={<DisplayAmount value={data?.allocated_revenue} />}
              info={DASHBOARD_STATS_INFO.allocatedRevenue}
            />
            <EvtStatsCard
              label="Billable Revenue"
              value={<DisplayAmount value={data?.billable_revenue} />}
              info={DASHBOARD_STATS_INFO.billableRevenue}
            />
            <EvtStatsCard
              label="Billed Revenue"
              value={<DisplayAmount value={data?.billed_revenue} />}
              info={DASHBOARD_STATS_INFO.billedRevenue}
            />
            <EvtStatsCard
              label="Net Cost"
              value={<DisplayAmount value={data?.net_cost} />}
              info={DASHBOARD_STATS_INFO.netCost}
            />
            <EvtStatsCard
              label="Achieved Margin"
              value={<DisplayAmount value={data?.achieved_margin_inr} />}
              info={DASHBOARD_STATS_INFO.achievedRevenue}
            />
            <EvtStatsCard
              label="Inventory"
              value={<DisplayAmount value={data?.inventory} />}
              info={DASHBOARD_STATS_INFO.inventory}
            />
            <EvtStatsCard
              label="Prepaid/Advance"
              value={<DisplayAmount value={data?.prepaid} />}
              info={DASHBOARD_STATS_INFO.prepaid}
            />
            <EvtStatsCard
              label="Backlog"
              value={<DisplayAmount value={data?.backlog} />}
              info={DASHBOARD_STATS_INFO.backlog}
            />
          </>
        )}
      </DashboardStatsSubSection>

      <DashboardStatsSubSection title="Delivery">
        <EvtStatsCard
          label="Approved vs Logged"
          value={
            <>
              <DisplayHours value={data?.approved_hours} hoursOnly /> /{" "}
              <DisplayHours value={data?.logged_hours} hoursOnly />
            </>
          }
          info={DASHBOARD_STATS_INFO.approvedVsLogged}
        />
      </DashboardStatsSubSection>
    </DashboardStatsGroup>
    </>
  );
};
